(function (global) {
  'use strict';

  const ANALYTICS_STATE_KEY = 'pet-food-australia.shepherd-analytics';
  const DEFAULT_ADVISOR_SESSION_KEY = 'pet-food-australia.advisor';
  const EVENT_SCHEMA_VERSION = '1.0.0';
  const ANALYTICS_STATE_VERSION = '1.0.0';

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function safeParse(value) {
    if (typeof value !== 'string' || !value) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function getStorage() {
    return typeof global.sessionStorage !== 'undefined'
      ? global.sessionStorage
      : null;
  }

  function createUuid() {
    const cryptoApi = global.crypto;

    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }

    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
      return null;
    }

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, function (byte) {
      return byte.toString(16).padStart(2, '0');
    }).join('');

    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20)
    ].join('-');
  }

  function getAcquisition() {
    const location = global.location;
    const document = global.document;
    let params = null;

    try {
      params = location && typeof location.href === 'string'
        ? new URL(location.href).searchParams
        : null;
    } catch (error) {
      params = null;
    }

    const readParam = function (key) {
      return params && typeof params.get === 'function'
        ? params.get(key)
        : null;
    };

    return {
      landing_page:
        location && typeof location.href === 'string'
          ? location.href
          : null,
      referrer:
        document &&
        typeof document.referrer === 'string' &&
        document.referrer
          ? document.referrer
          : null,
      utm_source: readParam('utm_source'),
      utm_medium: readParam('utm_medium'),
      utm_campaign: readParam('utm_campaign'),
      utm_content: readParam('utm_content'),
      utm_term: readParam('utm_term')
    };
  }

  function readAnalyticsState() {
    const storage = getStorage();

    if (!storage) {
      return null;
    }

    try {
      const parsed = safeParse(
        storage.getItem(ANALYTICS_STATE_KEY)
      );

      return isPlainObject(parsed) &&
        parsed.version === ANALYTICS_STATE_VERSION
        ? parsed
        : null;
    } catch (error) {
      return null;
    }
  }

  function writeAnalyticsState(state) {
    const storage = getStorage();

    if (!storage) {
      return false;
    }

    try {
      storage.setItem(
        ANALYTICS_STATE_KEY,
        JSON.stringify(state)
      );
      return true;
    } catch (error) {
      return false;
    }
  }

  function ensureAnalyticsState() {
    const existing = readAnalyticsState();

    if (
      existing &&
      typeof existing.shepherd_session_id === 'string'
    ) {
      return existing;
    }

    const sessionId = createUuid();

    if (!sessionId) {
      return null;
    }

    const state = {
      version: ANALYTICS_STATE_VERSION,
      shepherd_session_id: sessionId,
      acquisition: getAcquisition(),
      identity: {},
      pending_events: []
    };

    writeAnalyticsState(state);
    return state;
    }
    function setAnalyticsIdentity(identity) {
  const state = ensureAnalyticsState();

  if (!state || !isPlainObject(identity)) {
    return null;
  }

  state.identity = {
    ...(isPlainObject(state.identity) ? state.identity : {}),
    ...identity
  };

  writeAnalyticsState(state);
  return state;
}


  function resetAnalyticsState() {
    const storage = getStorage();

    if (!storage) {
      return;
    }

    try {
      storage.removeItem(ANALYTICS_STATE_KEY);
    } catch (error) {
      // Analytics must never interrupt Shepherd.
    }
  }

  function getAdvisorSessionKey(config) {
    return config &&
      config.appSettings &&
      typeof config.appSettings.sessionStorageKey === 'string'
      ? config.appSettings.sessionStorageKey
      : DEFAULT_ADVISOR_SESSION_KEY;
  }

  function readAdvisorSession(config) {
    const storage = getStorage();

    if (!storage) {
      return null;
    }

    try {
      const parsed = safeParse(
        storage.getItem(getAdvisorSessionKey(config))
      );

      return isPlainObject(parsed) ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function getQuestionForScreen(config, screenId) {
    const screens =
      config && Array.isArray(config.screens)
        ? config.screens
        : [];

    const questions =
      config && Array.isArray(config.questions)
        ? config.questions
        : [];

    const screen =
      screens.find(function (candidate) {
        return candidate && candidate.id === screenId;
      }) || null;

    if (!screen || typeof screen.questionId !== 'string') {
      return {
        screen: screen,
        question: null
      };
    }

    const question =
      questions.find(function (candidate) {
        return candidate &&
          candidate.id === screen.questionId;
      }) || null;

    return {
      screen: screen,
      question: question
    };
  }

  function buildRecommendationSnapshot(
    selectedRecommendation
  ) {
    const selected = isPlainObject(selectedRecommendation)
      ? selectedRecommendation
      : {};

    const objective = isPlainObject(
      selected.objectiveCalculations
    )
      ? selected.objectiveCalculations
      : {};

    const metadata = isPlainObject(
      selected.recommendationMetadata
    )
      ? selected.recommendationMetadata
      : {};

    const feeding = isPlainObject(
      selected.feedingCostSummary
    )
      ? selected.feedingCostSummary
      : {};

    return {
      status:
        typeof selected.status === 'string'
          ? selected.status
          : null,

      recommended_product_id:
        objective.recommendedProductId || null,

      recommended_variant_id:
        objective.recommendedVariantId || null,

      best_value_variant_id:
        objective.bestValueVariantId || null,

      reason_codes:
        Array.isArray(
          metadata.recommendationReasonCodes
        )
          ? metadata.recommendationReasonCodes
          : [],

      priority:
        typeof metadata.recommendationPriority === 'number'
          ? metadata.recommendationPriority
          : null,

      confidence:
        metadata.recommendationConfidence || null,

      version:
        metadata.recommendationVersion || null,

      policy_id:
        metadata.recommendationPolicyId || null,

      daily_feeding_grams:
        typeof feeding.dailyGrams === 'number'
          ? feeding.dailyGrams
          : null,

      cost_per_day_aud:
        typeof feeding.costPerDayAUD === 'number'
          ? feeding.costPerDayAUD
          : null,

      bag_duration_days:
        typeof objective.bagDurationDays === 'number'
          ? objective.bagDurationDays
          : null,

      reorder_after_days:
        typeof objective.reorderAfterDays === 'number'
          ? objective.reorderAfterDays
          : null,

      subscription_savings:
        typeof objective.subscriptionSavings === 'number'
          ? objective.subscriptionSavings
          : null,

      savings_vs_smaller_bag:
        typeof objective.savingsVsSmallerBag === 'number'
          ? objective.savingsVsSmallerBag
          : null,

      free_shipping_qualified:
        typeof objective.freeShippingQualified === 'boolean'
          ? objective.freeShippingQualified
          : null
    };
  }

  function buildSessionSnapshot(
    config,
    advisorSession,
    analyticsState,
    container
  ) {
    const session = isPlainObject(advisorSession)
      ? advisorSession
      : {};

    const progress = isPlainObject(session.progress)
      ? session.progress
      : {};

    const metadata = isPlainObject(session.metadata)
      ? session.metadata
      : {};

 const customerId =
  container && typeof container.getAttribute === 'function'
    ? container.getAttribute('data-shepherd-customer-id')
    : null;

const customerEmail =
  container && typeof container.getAttribute === 'function'
    ? container.getAttribute('data-shepherd-customer-email')
    : null;

const customerFirstName =
  container && typeof container.getAttribute === 'function'
    ? container.getAttribute('data-shepherd-customer-first-name')
    : null;
       return {
identity: customerEmail
  ? {
      identification_status: 'identified',
      identification_source: 'shopify_logged_in',
      email: customerEmail,
      customer_name: customerFirstName || null,
      shopify_customer_id: customerId || null
    }
  : (
      analyticsState &&
      isPlainObject(analyticsState.identity) &&
      analyticsState.identity.email
        ? analyticsState.identity
        : {}
    ),
      session_schema_version:
        session.schemaVersion || null,

      questionnaire_id:
        config &&
        config.questionnaireIntegration &&
        config.questionnaireIntegration.questionnaireId
          ? config.questionnaireIntegration.questionnaireId
          : 'nutrition-advisor',

      questionnaire_version:
        config && config.schemaVersion
          ? config.schemaVersion
          : null,

      started_at:
        metadata.startedAt || null,

      current_step:
        Number.isInteger(progress.currentStep)
          ? progress.currentStep
          : null,

      last_screen_id:
        session.currentScreenId || null,

      answers:
        isPlainObject(session.answers)
          ? session.answers
          : {},

      current_food:
        isPlainObject(session.currentFoodProfile)
          ? session.currentFoodProfile
          : {},

      recommendation:
        buildRecommendationSnapshot(
          session.selectedRecommendation
        ),

      acquisition:
        analyticsState &&
        isPlainObject(analyticsState.acquisition)
          ? analyticsState.acquisition
          : {},

      metadata: {
        analytics_state_version:
          ANALYTICS_STATE_VERSION
      }
    };
  }

  function getTransportConfig(container) {
    if (
      !container ||
      typeof container.getAttribute !== 'function'
    ) {
      return null;
    }

    const endpoint =
      container.getAttribute(
        'data-shepherd-analytics-endpoint'
      );

    const publishableKey =
      container.getAttribute(
        'data-shepherd-analytics-publishable-key'
      );

    if (!endpoint || !publishableKey) {
      return null;
    }

    return {
      endpoint: endpoint.trim(),
      publishableKey: publishableKey.trim()
    };
  }

  function enqueueEvent(state, payload) {
    if (!state) {
      return;
    }

    const pending = Array.isArray(state.pending_events)
      ? state.pending_events
      : [];

    if (
      !pending.some(function (item) {
        return (
          item &&
          item.client_event_id === payload.client_event_id
        );
      })
    ) {
      pending.push(payload);
    }

    state.pending_events = pending.slice(-50);
    writeAnalyticsState(state);
  }

  function removePendingEvent(state, clientEventId) {
    if (
      !state ||
      !Array.isArray(state.pending_events)
    ) {
      return;
    }

    state.pending_events =
      state.pending_events.filter(function (item) {
        return (
          !item ||
          item.client_event_id !== clientEventId
        );
      });

    writeAnalyticsState(state);
  }

  function deliverPayload(container, state, payload) {
    const transport = getTransportConfig(container);

    if (
      !transport ||
      typeof global.fetch !== 'function'
    ) {
      enqueueEvent(state, payload);
      return Promise.resolve(false);
    }

    return global
      .fetch(transport.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: transport.publishableKey
        },
        body: JSON.stringify(payload),
        keepalive: true
      })
      .then(function (response) {
        if (!response || !response.ok) {
          throw new Error(
            'analytics delivery failed'
          );
        }

        removePendingEvent(
          state,
          payload.client_event_id
        );

        return true;
      })
      .catch(function () {
        enqueueEvent(state, payload);
        return false;
      });
  }

  function flushPendingEvents(container) {
    const state = ensureAnalyticsState();

    if (
      !state ||
      !Array.isArray(state.pending_events) ||
      !state.pending_events.length
    ) {
      return Promise.resolve([]);
    }

    const pending = state.pending_events.slice();

    return Promise.all(
      pending.map(function (payload) {
        return deliverPayload(
          container,
          state,
          payload
        );
      })
    );
  }

  function createEventPayload(
    config,
    advisorSession,
    container,
    eventName,
    clientSuffix,
    properties
  ) {
    const state = ensureAnalyticsState();

    if (!state) {
      return null;
    }

    return {
      shepherd_session_id:
        state.shepherd_session_id,

      event_name:
        eventName,

      client_event_id: [
        state.shepherd_session_id,
        eventName,
        clientSuffix
      ].join(':'),

      event_schema_version:
        EVENT_SCHEMA_VERSION,
occurred_at:
  new Date().toISOString(),
      session:
        buildSessionSnapshot(
          config,
          advisorSession,
          state,
          container
        ),

      properties:
        isPlainObject(properties)
          ? properties
          : {}
    };
  }

  function trackEvent(
    config,
    advisorSession,
    container,
    eventName,
    clientSuffix,
    properties
  ) {
    const state = ensureAnalyticsState();

    const payload = createEventPayload(
      config,
      advisorSession,
      container,
      eventName,
      clientSuffix,
      properties
    );

    if (!state || !payload) {
      return Promise.resolve(false);
    }

    enqueueEvent(state, payload);

    return deliverPayload(
      container,
      state,
      payload
    );
  }

  function trackAcceptedTransition(
    config,
    previousSession,
    nextSession,
    container
  ) {
    if (
      !isPlainObject(previousSession) ||
      !isPlainObject(nextSession)
    ) {
      return;
    }

    const previousScreenId =
      previousSession.currentScreenId;

    const nextScreenId =
      nextSession.currentScreenId;

    if (
      !previousScreenId ||
      previousScreenId === nextScreenId
    ) {
      return;
    }

    if (previousScreenId === 'welcome') {
      trackEvent(
        config,
        previousSession,
        container,
        'shepherd_started',
        'start',
        {}
      );
    }

    const questionContext =
      getQuestionForScreen(
        config,
        previousScreenId
      );

    if (questionContext.question) {
      const question =
        questionContext.question;

      const answerKey =
        question.answerKey ||
        questionContext.screen.questionId ||
        question.id;

      const answerValue =
        nextSession.answers &&
        Object.prototype.hasOwnProperty.call(
          nextSession.answers,
          answerKey
        )
          ? nextSession.answers[answerKey]
          : null;

      trackEvent(
        config,
        nextSession,
        container,
        'shepherd_step_completed',
        'step:' + previousScreenId,
        {
          screen_id:
            previousScreenId,

          question_id:
            question.id ||
            questionContext.screen.questionId ||
            null,

          analytics_key:
            question.analyticsKey ||
            answerKey,

          answer_value:
            answerValue,

          step_number:
            nextSession.progress &&
            Number.isInteger(
              nextSession.progress.currentStep
            )
              ? nextSession.progress.currentStep - 1
              : null
        }
      );
    }

    if (nextScreenId === 'results') {
      trackEvent(
        config,
        nextSession,
        container,
        'shepherd_completed',
        'complete',
        {}
      );

      if (
        container &&
        typeof container.querySelector === 'function' &&
        container.querySelector(
          '.nutrition-advisor__results'
        )
      ) {
        trackEvent(
          config,
          nextSession,
          container,
          'shepherd_recommendation_viewed',
          'view',
          {}
        );
      }
    }
  }

  function loadConfig(container) {
    if (
      !container ||
      typeof container.getAttribute !== 'function'
    ) {
      return Promise.resolve(null);
    }

    const configUrl =
      container.getAttribute('data-config-url');

    if (
      !configUrl ||
      typeof global.fetch !== 'function'
    ) {
      return Promise.resolve(null);
    }

    return global
      .fetch(configUrl)
      .then(function (response) {
        return response && response.ok
          ? response.json()
          : null;
      })
      .catch(function () {
        return null;
      });
  }

  function cloneSession(value) {
    if (!isPlainObject(value)) {
      return null;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return null;
    }
  }

  function setup(container, configPromise) {
    if (
      !container ||
      typeof container.addEventListener !== 'function'
    ) {
      return;
    }

    ensureAnalyticsState();
    const customerEmail = container.getAttribute(
  'data-shepherd-customer-email'
);

if (customerEmail) {
  setAnalyticsIdentity({
    identification_status: 'identified',
    identification_source: 'shopify_logged_in',
    email: customerEmail,
    customer_name:
      container.getAttribute(
        'data-shepherd-customer-first-name'
      ) || null,
    shopify_customer_id:
      container.getAttribute(
        'data-shepherd-customer-id'
      ) || null
  });
}
    flushPendingEvents(container);
setupPlanEmailCapture(container, configPromise);
    container.addEventListener(
      'click',
      function (event) {
        const target = event.target;

        if (
          !target ||
          typeof target.matches !== 'function'
        ) {
          return;
        }

        if (
          target.matches(
            '[data-result-action="start-again"]'
          )
        ) {
          global.setTimeout(function () {
            resetAnalyticsState();
          }, 0);

          return;
        }

        if (
          !target.matches(
            '[data-next="true"], [data-answer]'
          )
        ) {
          return;
        }

        const provisionalConfig = null;

        const previousSession =
          cloneSession(
            readAdvisorSession(
              provisionalConfig
            )
          );

        global.setTimeout(function () {
          Promise.resolve(configPromise)
            .then(function (config) {
              if (!config) {
                return;
              }

              const nextSession =
                cloneSession(
                  readAdvisorSession(config)
                );

              trackAcceptedTransition(
                config,
                previousSession,
                nextSession,
                container
              );

            
            })
            .catch(function () {
              // Analytics must never interrupt Shepherd.
            });
        }, 0);
      },
      true
    );
  }
function setupPlanEmailCapture(container, configPromise) {
  if (
    !container ||
    typeof container.addEventListener !== 'function'
  ) {
    return;
  }

  container.addEventListener('submit', function (event) {
    const form = event.target;

    if (
      !form ||
      typeof form.matches !== 'function' ||
      !form.matches('[data-shepherd-email-plan-form]')
    ) {
      return;
    }

    event.preventDefault();

    const input = form.querySelector(
      '[data-shepherd-email-plan-input]'
    );

    if (!input || !input.value) {
      return;
    }

    const email = String(input.value).trim();

    if (!email) {
      return;
    }

    const state = ensureAnalyticsState();

if (!state) {
  return;
}

state.identity = {
  ...(isPlainObject(state.identity) ? state.identity : {}),
  identification_status: 'identified',
  identification_source: 'shepherd_email_capture',
  email: email,
};

writeAnalyticsState(state);

const button = form.querySelector('button[type="submit"]');

if (button) {
  button.disabled = true;
  button.textContent = 'Sending...';
}

Promise.resolve(configPromise)
  .then(function (config) {
    if (!config) {
      throw new Error('Shepherd config unavailable');
    }

    const advisorSession =
      cloneSession(readAdvisorSession(config)) || {};

    advisorSession.identity = {
      ...(isPlainObject(advisorSession.identity)
        ? advisorSession.identity
        : {}),
      identification_status: 'identified',
      identification_source: 'shepherd_email_capture',
      email: email,
    };

    return trackEvent(
      config,
      advisorSession,
      container,
      'shepherd_email_captured',
      'email-captured',
      {
        source: 'results_email_plan'
      }
    );
  })
  .then(function (sent) {
    if (!sent) {
      throw new Error('Shepherd email event failed');
    }

    input.disabled = true;

    if (button) {
      button.disabled = true;
      button.textContent = 'Plan sent to your inbox';
    }
  })
  .catch(function () {
    if (button) {
      button.disabled = false;
      button.textContent = 'Email my plan';
    }
  });
  });
}
  function bootstrap() {
    if (typeof global.document === 'undefined') {
      return;
    }

    const container =
      global.document.getElementById(
        'nutrition-advisor-app'
      );

    if (!container) {
      return;
    }

    const configPromise =
      loadConfig(container);

    setup(container, configPromise);
  }

  const api = {
    createUuid: createUuid,
    getAcquisition: getAcquisition,
    ensureAnalyticsState: ensureAnalyticsState,
    resetAnalyticsState: resetAnalyticsState,
    buildRecommendationSnapshot:
      buildRecommendationSnapshot,
    buildSessionSnapshot:
      buildSessionSnapshot,
    createEventPayload:
      createEventPayload,
    trackAcceptedTransition:
      trackAcceptedTransition,
    flushPendingEvents:
      flushPendingEvents,
    setup: setup
  };

  if (
    typeof module !== 'undefined' &&
    module.exports
  ) {
    module.exports = api;
  }

  global.ShepherdAnalytics = api;

  if (typeof global.document !== 'undefined') {
    global.document.addEventListener(
      'DOMContentLoaded',
      bootstrap
    );
  }
})(
  typeof globalThis !== 'undefined'
    ? globalThis
    : this
);